import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { ProjectResponse, SkillResult } from '@/types';
import { Search, X, Loader2, BookOpen, Layers, Plus } from 'lucide-react';
import OwnOrganizationFrameWorks from './own-organization-frameworks';
import UnderlinedTabs from '@/components/tabs';
import { useState, useEffect } from 'react';
import PublicFrameWorks from './public-frameworks';
import { useTenant } from '@/hooks';
import { useSearchParams } from 'react-router-dom';
import { useClientPermissions } from '@/hooks/use-client-permissions';
import { useGetFilteredSkillsQuery } from '@/state/skills';
import {
  useUpdateInternalProjectMutation,
  useAddProjectSkillMutation,
  useDeleteProjectSkillMutation,
  useUpdateExternalProjectMutation,
  useAddExternalProjectSkillMutation,
  useDeleteExternalProjectSkillMutation
} from '@/state';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface Props {
  projectDetails: ProjectResponse;
  isReadOnly?: boolean;
}

const AddSkills: React.FC<Props> = ({ projectDetails, isReadOnly }) => {
  const { tenant } = useTenant();
  const [isWantToManageFrameworks, setIsWantToManageFrameworks] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedLevels, setSelectedLevels] = useState<Record<number, number>>({});
  const [skillToConfirm, setSkillToConfirm] = useState<SkillResult | null>(null);

  const [addSkill] = useAddProjectSkillMutation();
  const [deleteSkill] = useDeleteProjectSkillMutation();
  const [updateProject] = useUpdateInternalProjectMutation();

  const [addExternalSkill] = useAddExternalProjectSkillMutation();
  const [deleteExternalSkill] = useDeleteExternalProjectSkillMutation();
  const [updateExternalProject] = useUpdateExternalProjectMutation();

  const [selectedOrgIds, setSelectedOrgIds] = useState<number[]>(
    projectDetails.skill_framework?.map(s => s.id) || []
  );
  const [selectedGlobalIds, setSelectedGlobalIds] = useState<number[]>(
    projectDetails.global_skill_framework?.map(s => s.id) || []
  );

  const { isCompany } = useClientPermissions();
  const [searchParams] = useSearchParams();
  const isExternal = searchParams.get('type') === 'EXTERNAL';
  const canManage = (!isCompany && !isReadOnly) || true

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const { data: searchResults, isFetching } = useGetFilteredSkillsQuery(
    {
      tenant: tenant!,
      params: {
        search: debouncedSearch,
        parent: [0, ...selectedOrgIds, ...selectedGlobalIds],
      },
    },
    { skip: !tenant || debouncedSearch.length < 1 }
  );

  const existingSkillIds = new Set(
    (projectDetails.expected_skills ?? [])
      .map((skill) => skill.skill_id ?? skill.skill ?? skill.id)
      .filter((id): id is number => typeof id === 'number' && Number.isFinite(id))
  );

  const visibleSearchResults = useMemo(
    () =>
      (searchResults?.results ?? []).filter(
        (skill) => !existingSkillIds.has(skill.skill_id)
      ),
    [existingSkillIds, searchResults?.results]
  );

  const handleToggleFramework = async (id: number, type: 'org' | 'global') => {
    const isOrg = type === 'org';
    const current = isOrg ? selectedOrgIds : selectedGlobalIds;
    const next = current.includes(id) ? current.filter(i => i !== id) : [...current, id];

    if (isOrg) setSelectedOrgIds(next);
    else setSelectedGlobalIds(next);

    const nextOrg = isOrg ? next : selectedOrgIds;
    const nextGlobal = isOrg ? selectedGlobalIds : next;

    try {
      if (isExternal && isCompany) {
        await updateExternalProject({
          tenant: tenant!,
          projectId: projectDetails.project_id || projectDetails.id,
          data: {
            skill_framework: nextOrg,
            global_skill_framework: nextGlobal
          }
        }).unwrap();
      } else {
        await updateProject({
          tenant: tenant!,
          projectId: projectDetails.id,
          data: {
            skill_framework: nextOrg,
            global_skill_framework: nextGlobal
          } as any
        }).unwrap();
      }
      toast.success('Project frameworks updated');
    } catch (err) {
      toast.error('Failed to update frameworks');
    }
  };

  const handleAddSkill = async (skill: SkillResult) => {
    // Check if project is active or completed (status > 1)
    // 1: Pending, 2: Assigned, 3: Ready, 4: In Progress, 5: Completed
    const status = Number(projectDetails.project_status);
    if (status > 1) {
      setSkillToConfirm(skill);
      return;
    }

    await executeAddSkill(skill);
  };

  const executeAddSkill = async (skill: SkillResult) => {
    const level = selectedLevels[skill.id] || 1;
    const projectId = projectDetails.id;

    try {
      if (isExternal && isCompany) {
        await addExternalSkill({
          tenant: tenant!,
          projectId: projectDetails.project_id || projectDetails.id,
          data: {
            skills: [
              {
                project: projectDetails.project_id || projectDetails.id,
                skill: skill.skill_id,
                required_level: level,
                skill_name: skill.name
              }
            ]
          }
        }).unwrap();
      } else {
        await addSkill({
          tenant: tenant!,
          projectId: projectDetails.id,
          data: {
            skills: [
              {
                project: projectId,
                skill: skill.skill_id,
                required_level: level,
                skill_name: skill.name
              }
            ]
          }
        }).unwrap();
      }
      toast.success(`${skill.name} (Level ${level}) added`);
      setSkillToConfirm(null);
    } catch (err: any) {
      const errorMsg = err?.data?.skills?.[0]?.project?.[0] ||
        err?.data?.skills?.[0]?.skill?.[0] ||
        'Failed to add skill';
      toast.error(errorMsg);
    }
  };

  const handleDeleteSkill = async (id: number) => {
    try {
      if (isExternal && isCompany) {
        await deleteExternalSkill({
          tenant: tenant!,
          id,
          projectId: projectDetails.project_id || projectDetails.id
        }).unwrap();
      } else {
        await deleteSkill({
          tenant: tenant!,
          id,
          projectId: projectDetails.id
        }).unwrap();
      }
      toast.success('Skill removed');
    } catch (err) {
      toast.error('Failed to remove skill');
    }
  };

  const handleLevelChange = (skillId: number, level: number) => {
    setSelectedLevels(prev => ({ ...prev, [skillId]: level }));
  };

  const tabs = [
    {
      value: 'org',
      title: 'Organization Framework',
      icon: <BookOpen size={18} />,
      content: <OwnOrganizationFrameWorks
        selectedFrameworks={selectedOrgIds}
        onToggle={(id) => handleToggleFramework(id, 'org')}
      />,
    },
    {
      value: 'public',
      title: 'Public Frameworks',
      icon: <Layers size={18} />,
      content: <PublicFrameWorks
        selectedFrameworks={selectedGlobalIds}
        onToggle={(id) => handleToggleFramework(id, 'global')}
      />,
    },
  ];

  return (
    <Dialog>
      {canManage && (
        <DialogTrigger asChild>
          <Button className=" px-6 shadow-lg hover:shadow-primary/20 transition-all duration-300">
            <Plus size={18} /> Add Skills
          </Button>
        </DialogTrigger>
      )}

      <DialogContent className="max-w-[1000px] w-[95vw] rounded-3xl overflow-hidden p-0 border-none shadow-2xl overflow-y-auto max-h-[90vh]">
        <div className="p-6 md:p-8">
          <DialogHeader className="mb-6">
            <DialogTitle className="text-3xl font-bold tracking-tight">Manage Project Skills</DialogTitle>
          </DialogHeader>

          <div className="space-y-8">
            {/* Search Section */}
            <div className="relative group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
              <Input
                placeholder="Search skills (e.g. Creativity, Analysis...)"
                className="pl-12 py-7 rounded-2xl border-2 border-muted focus-visible:ring-primary/20 text-lg transition-all"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {isFetching && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 animate-spin text-primary" />}
            </div>

            {/* Search Results */}
            {searchTerm.length >= 1 && (
              <div className="bg-muted/30 rounded-3xl p-6 border border-border/50 animate-in fade-in slide-in-from-top-4 duration-300">
                <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Search Results</h4>
                <div className="flex flex-col gap-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                  {visibleSearchResults.map((skill) => {
                    const currentLevel = selectedLevels[skill.id] || 1;

                    return (
                      <div
                        key={skill.id}
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl bg-background border border-border hover:border-primary hover:shadow-md transition-all text-left gap-4"
                      >
                        <div className="flex-1">
                          <p className="font-semibold text-sm leading-snug">{skill.name}</p>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-tight mt-1">
                            {skill.parent?.name || 'Root Framework'}
                          </p>
                        </div>

                        <div className="flex items-center gap-4 shrink-0">
                          <div className="flex items-center gap-2">
                            <div className="flex bg-muted rounded-lg p-0.5 border border-border/50">
                              {[1, 2, 3, 4, 5].map((level) => (
                                <button
                                  key={level}
                                  onClick={() => handleLevelChange(skill.id, level)}
                                  className={cn(
                                    "w-7 h-7 rounded-md text-[10px] font-bold transition-all",
                                    currentLevel === level
                                      ? "bg-primary text-blue-500 shadow-sm"
                                      : "hover:bg-background text-muted-foreground"
                                  )}
                                >
                                  {level}
                                </button>
                              ))}
                            </div>
                          </div>

                          <Button
                            size="sm"
                            disabled={!canManage}
                            className={cn("rounded-lg px-6 h-8 font-bold")}
                            onClick={() => handleAddSkill(skill)}
                          >
                            Add
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  {visibleSearchResults.length === 0 && !isFetching && (
                    <p className="text-center py-8 text-muted-foreground italic">No matching skills found in selected frameworks.</p>
                  )}
                </div>
              </div>
            )}

            {/* Selected Summary */}
            <div className="space-y-4 bg-background/50 backdrop-blur-sm rounded-3xl p-6 border border-border/40">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Framework Selection</h3>
                <Button
                  variant={isWantToManageFrameworks ? "destructive" : "outline"}
                  size="sm"
                  className=" px-4 h-9 font-semibold transition-all hover:scale-105"
                  onClick={() => setIsWantToManageFrameworks(!isWantToManageFrameworks)}
                >
                  {isWantToManageFrameworks ? "Hide Selection" : "Browse Frameworks"}
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                {projectDetails.skill_framework?.map((s) => (
                  <Badge key={s.id} variant="secondary" className="pl-3 pr-2 py-1.5 gap-2  bg-blue-500/10 text-blue-700 border-blue-500/20">
                    <BookOpen size={14} className="opacity-70" />
                    {s.name}
                    {canManage && (
                      <X className="w-4 h-4 cursor-pointer hover:text-red-500" onClick={() => handleToggleFramework(s.id, 'org')} />
                    )}
                  </Badge>
                ))}
                {projectDetails.global_skill_framework?.map((s) => (
                  <Badge key={s.id} variant="secondary" className="pl-3 pr-2 py-1.5 gap-2  bg-emerald-500/10 text-emerald-700 border-emerald-500/20">
                    <Layers size={14} className="opacity-70" />
                    {s.name}
                    {canManage && (
                      <X className="w-4 h-4 cursor-pointer hover:text-red-500" onClick={() => handleToggleFramework(s.id, 'global')} />
                    )}
                  </Badge>
                ))}
                {(!projectDetails.skill_framework?.length && !projectDetails.global_skill_framework?.length) && (
                  <p className="text-xs text-muted-foreground italic">Please select at least one framework to start searching skills.</p>
                )}
              </div>

              {/* Skills Display */}
              <div className="pt-6 border-t border-border/50">
                <h4 className="text-[10px] font-bold uppercase text-muted-foreground mb-4 tracking-[0.2em]">Added Project Skills</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {projectDetails.expected_skills?.map((skill) => {
                    const skillKey = skill.id || skill.skill_id;
                    return (
                      <div key={skillKey} className="flex items-center justify-between p-3.5 rounded-2xl bg-white/50 border border-border/60 hover:shadow-sm transition-all group">
                        <div className="truncate">
                          <p title={skill.skill_name} className="text-xs font-bold truncate group-hover:text-primary transition-colors">{skill.skill_name}</p>
                          <Badge variant="outline" className="mt-1.5 h-5 text-[10px] font-medium bg-primary/5 text-primary border-primary/20">
                            Level {skill.required_level}
                          </Badge>
                        </div>
                        {canManage && (
                          <button
                            onClick={() => skillKey && typeof skillKey === 'number' && handleDeleteSkill(skillKey)}
                            disabled={!skill.id && !skill.skill_id}
                            className={cn(
                              "text-muted-foreground hover:text-red-500 p-1.5  hover:bg-red-50 transition-all opacity-0 group-hover:opacity-100",
                              (!skill.id && !skill.skill_id) && "cursor-not-allowed hidden"
                            )}
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {(!projectDetails.expected_skills?.length) && (
                    <div className="col-span-full py-4 text-center border-2 border-dashed border-muted rounded-2xl">
                      <p className="text-xs text-muted-foreground italic">Search and add skills from the box above.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Management Section */}
            {isWantToManageFrameworks && (
              <div className="bg-background rounded-3xl p-6 border-2 border-primary/20 shadow-xl animate-in zoom-in-95 duration-300">
                <UnderlinedTabs tabItems={tabs} subTab />
              </div>
            )}
          </div>
        </div>
      </DialogContent>

      <AlertDialog open={!!skillToConfirm} onOpenChange={() => setSkillToConfirm(null)}>
        <AlertDialogContent className="rounded-3xl border-2 border-primary/20 shadow-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold text-foreground">Warning!</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground font-medium">
              Adding new skills in an active project might affect learners’ skills evaluations.
              Please continue at your own risk!
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className=" border-2">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => skillToConfirm && executeAddSkill(skillToConfirm)}
              className=" bg-primary hover:bg-primary/90 text-primary-foreground font-bold shadow-lg shadow-primary/25"
            >
              Continue Adding
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

export default AddSkills;
